/**
 * Desktop Checkout Interactions
 * Enhanced Add to Cart experience with animations, notifications, and user feedback
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    enhanceAddToCartButton();
    enhanceQuantitySelector();
    addSuccessToast();
    addTrustSignals();
    trackButtonInteractions();
  }

  /**
   * Enhance Add to Cart button with better feedback
   */
  function enhanceAddToCartButton() {
    const submitButtons = document.querySelectorAll('.product-form__submit');

    submitButtons.forEach(button => {
      // Add icon to button text
      const span = button.querySelector('span');
      if (span && !span.querySelector('.cart-icon')) {
        const icon = document.createElement('span');
        icon.className = 'cart-icon';
        icon.innerHTML = '🛒';
        icon.style.marginLeft = '0.6rem';
        icon.style.transition = 'transform 0.3s ease';
        span.appendChild(icon);
      }

      // Hover animation for icon
      button.addEventListener('mouseenter', () => {
        const icon = button.querySelector('.cart-icon');
        if (icon) {
          icon.style.transform = 'scale(1.2) rotate(5deg)';
        }
      });

      button.addEventListener('mouseleave', () => {
        const icon = button.querySelector('.cart-icon');
        if (icon) {
          icon.style.transform = 'scale(1) rotate(0deg)';
        }
      });
    });

    // Listen for successful add to cart
    document.addEventListener('cart:add', handleCartAdd);
  }

  /**
   * Handle successful cart addition
   */
  function handleCartAdd(event) {
    const button = document.querySelector('.product-form__submit.loading');

    if (button) {
      // Add success class temporarily
      setTimeout(() => {
        button.classList.add('success');
        setTimeout(() => {
          button.classList.remove('success');
        }, 600);
      }, 300);
    }

    // Show success toast
    showSuccessToast(event.detail);

    // Track conversion
    trackAddToCart(event.detail);
  }

  /**
   * Show success toast notification
   */
  function showSuccessToast(cartData) {
    // Remove existing toast if any
    const existingToast = document.querySelector('.cart-success-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast
    const toast = document.createElement('div');
    toast.className = 'cart-success-toast';
    toast.innerHTML = `
      <div class="cart-success-icon">
        <span>✓</span>
      </div>
      <div class="cart-success-content">
        <div class="cart-success-title">Added to cart!</div>
        <div class="cart-success-message">Item successfully added to your cart</div>
      </div>
      <a href="/cart" class="cart-success-view">View Cart</a>
    `;

    document.body.appendChild(toast);

    // Auto-hide after 4 seconds
    setTimeout(() => {
      toast.classList.add('hiding');
      setTimeout(() => {
        toast.remove();
      }, 300);
    }, 4000);

    // Click to dismiss
    toast.addEventListener('click', (e) => {
      if (!e.target.closest('.cart-success-view')) {
        toast.classList.add('hiding');
        setTimeout(() => {
          toast.remove();
        }, 300);
      }
    });
  }

  /**
   * Enhance quantity selector with better feedback
   */
  function enhanceQuantitySelector() {
    const quantityInputs = document.querySelectorAll('.quantity__input');

    quantityInputs.forEach(input => {
      let previousValue = input.value;

      input.addEventListener('change', () => {
        if (input.value !== previousValue) {
          input.classList.add('changed');
          setTimeout(() => {
            input.classList.remove('changed');
          }, 300);
          previousValue = input.value;

          // Track quantity change
          if (typeof gtag !== 'undefined') {
            gtag('event', 'quantity_change', {
              'event_category': 'Product',
              'event_label': input.value,
              'value': parseInt(input.value)
            });
          }
        }
      });
    });

    // Enhance plus/minus buttons
    const quantityButtons = document.querySelectorAll('.quantity__button');
    quantityButtons.forEach(button => {
      button.addEventListener('click', () => {
        const input = button.parentElement.querySelector('.quantity__input');
        if (input) {
          setTimeout(() => {
            input.classList.add('changed');
            setTimeout(() => {
              input.classList.remove('changed');
            }, 300);
          }, 50);
        }
      });
    });
  }

  /**
   * Add trust signals below Add to Cart button
   */
  function addTrustSignals() {
    const productForm = document.querySelector('.product-form');
    if (!productForm) return;

    const buttonsContainer = productForm.querySelector('.product-form__buttons');
    if (!buttonsContainer) return;

    // Check if trust signals already exist
    if (buttonsContainer.querySelector('.product-trust-signals')) return;

    // Create trust signals
    const trustSignals = document.createElement('div');
    trustSignals.className = 'product-trust-signals';
    trustSignals.innerHTML = `
      <div class="trust-signal-item">
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <span>Secure Checkout</span>
      </div>
      <div class="trust-signal-item">
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
          <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
        </svg>
        <span>Free Shipping Over $50</span>
      </div>
      <div class="trust-signal-item">
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
        </svg>
        <span>Fast Delivery</span>
      </div>
    `;

    buttonsContainer.appendChild(trustSignals);
  }

  /**
   * Track button interactions for analytics
   */
  function trackButtonInteractions() {
    // Track Add to Cart button clicks
    const submitButtons = document.querySelectorAll('.product-form__submit');
    submitButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'add_to_cart_click', {
            'event_category': 'Ecommerce',
            'event_label': 'Desktop Add to Cart Button',
            'button_type': button.classList.contains('button--primary') ? 'primary' : 'secondary'
          });
        }

        if (typeof fbq !== 'undefined') {
          fbq('track', 'AddToCart', {
            content_type: 'product',
            content_category: 'button_click'
          });
        }
      });
    });

    // Track dynamic checkout button clicks
    const dynamicCheckoutButtons = document.querySelectorAll('.shopify-payment-button__button');
    dynamicCheckoutButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'express_checkout_click', {
            'event_category': 'Ecommerce',
            'event_label': 'Express Checkout Button'
          });
        }
      });
    });
  }

  /**
   * Track add to cart event
   */
  function trackAddToCart(cartData) {
    // Google Analytics 4
    if (typeof gtag !== 'undefined' && cartData && cartData.items) {
      const items = cartData.items.map(item => ({
        item_id: item.id,
        item_name: item.title,
        price: item.price / 100,
        quantity: item.quantity
      }));

      gtag('event', 'add_to_cart', {
        currency: 'USD',
        value: (cartData.total_price || 0) / 100,
        items: items
      });
    }

    // Facebook Pixel
    if (typeof fbq !== 'undefined' && cartData && cartData.items) {
      fbq('track', 'AddToCart', {
        content_type: 'product',
        contents: cartData.items.map(item => ({
          id: item.id,
          quantity: item.quantity
        })),
        value: (cartData.total_price || 0) / 100,
        currency: 'USD'
      });
    }
  }

  /**
   * Add inventory urgency indicator
   */
  function addInventoryUrgency() {
    const productForm = document.querySelector('.product-form');
    if (!productForm) return;

    const variantIdInput = productForm.querySelector('[name="id"]');
    if (!variantIdInput) return;

    const variantId = variantIdInput.value;

    // Fetch inventory (would need to be implemented with Shopify API)
    // For now, showing placeholder
    const inventory = Math.floor(Math.random() * 10) + 3; // Mock data

    if (inventory < 10) {
      const urgencyIndicator = document.createElement('div');
      urgencyIndicator.className = 'urgency-indicator';
      urgencyIndicator.textContent = `Only ${inventory} left in stock - Order soon!`;

      const buttonsContainer = productForm.querySelector('.product-form__buttons');
      if (buttonsContainer && !buttonsContainer.querySelector('.urgency-indicator')) {
        buttonsContainer.insertBefore(urgencyIndicator, buttonsContainer.firstChild);
      }
    }
  }

  /**
   * Add social proof badge
   */
  function addSocialProof() {
    const productInfo = document.querySelector('.product__info-wrapper');
    if (!productInfo) return;

    const viewersCount = Math.floor(Math.random() * 20) + 10; // Mock data

    const socialProof = document.createElement('div');
    socialProof.className = 'social-proof-badge';
    socialProof.innerHTML = `<strong>${viewersCount}</strong> people viewing this right now`;

    const price = productInfo.querySelector('.price');
    if (price && !productInfo.querySelector('.social-proof-badge')) {
      price.parentNode.insertBefore(socialProof, price.nextSibling);
    }
  }

  /**
   * Enhanced variant selection feedback
   */
  function enhanceVariantSelection() {
    const variantInputs = document.querySelectorAll('.product-form__input input[type="radio"]');

    variantInputs.forEach(input => {
      input.addEventListener('change', () => {
        // Add visual feedback
        const label = input.nextElementSibling;
        if (label) {
          label.style.transition = 'all 0.3s ease';
          label.style.transform = 'scale(1.05)';
          setTimeout(() => {
            label.style.transform = 'scale(1)';
          }, 300);
        }

        // Track variant change
        if (typeof gtag !== 'undefined') {
          gtag('event', 'variant_select', {
            'event_category': 'Product',
            'event_label': input.value
          });
        }
      });
    });
  }

  /**
   * Add keyboard shortcuts for power users
   */
  function addKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Ctrl/Cmd + Enter to add to cart
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        const submitButton = document.querySelector('.product-form__submit:not([disabled])');
        if (submitButton) {
          submitButton.click();

          // Show keyboard shortcut feedback
          const toast = document.createElement('div');
          toast.className = 'keyboard-shortcut-toast';
          toast.textContent = '⌨️ Keyboard shortcut used!';
          toast.style.cssText = `
            position: fixed;
            top: 2rem;
            right: 2rem;
            background: #1f2937;
            color: white;
            padding: 1rem 1.5rem;
            border-radius: 0.5rem;
            font-size: 1.4rem;
            z-index: 9999;
            animation: slideDown 0.3s ease;
          `;
          document.body.appendChild(toast);
          setTimeout(() => toast.remove(), 2000);
        }
      }

      // Plus key to increase quantity
      if (e.key === '+' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        const plusButton = document.querySelector('.quantity__button[name="plus"]');
        if (plusButton) plusButton.click();
      }

      // Minus key to decrease quantity
      if (e.key === '-' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        const minusButton = document.querySelector('.quantity__button[name="minus"]');
        if (minusButton) minusButton.click();
      }
    });
  }

  // Initialize additional features
  enhanceVariantSelection();
  addKeyboardShortcuts();

  // Optional: Uncomment to enable
  // addInventoryUrgency();
  // addSocialProof();

  // Listen for custom cart update events
  if (typeof PUB_SUB_EVENTS !== 'undefined') {
    document.addEventListener(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.detail && event.detail.source === 'product-form') {
        handleCartAdd(event);
      }
    });
  }

  // Smooth scroll to product form on direct links
  if (window.location.hash === '#product-form') {
    setTimeout(() => {
      const productForm = document.querySelector('.product-form');
      if (productForm) {
        productForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

})();
